import React, { useMemo, useState, useRef } from "react";
import { Container, Row, Col, Form } from "react-bootstrap";
import { CustomModal, TableHeader, TooltipPopover } from "@services/ui-components";
import { Trans, useTranslation } from "react-i18next";
import { ApolloError, useReactiveVar } from "@apollo/client";
import Spinner from "@services/ui-components/src/Spinner/Spinner";
import { useParams } from "react-router-dom";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import ErrorService from "../../../services/ErrorService";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { buildTariffPlansViewModel, getBenefitsRows } from "./helpers/tariffPlansViewModel";
import {
    useGetPaidFeaturesQuery,
    useGetPlanPriceQuery,
    useGeneratePaymentUrlMutation,
} from "@me-team/host/main/tariffPlans/graphql/tariffPlans.hooks";
import { PeriodCode, TariffPlansFilter, TariffPlanVm } from "../interface";
import TariffPlanFilter from "./TariffPlanFilter";
import TariffPlanCard from "./TariffPlanCard";
import TariffPlansBenefitsCard from "./TariffPlansBenefitsCard";
import InfoFooter from "../InfoFooter";
import useOnClickOutside from "../../../hooks/useOnClickOutside";
import { defaultCurrency } from "./helpers/constants";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

const TariffPlans: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { branchId } = useParams();
    const basePath = process.env.REACT_APP_POS_URL;
    const language = i18n?.language === "en" ? "" : `${i18n?.language}/`;

    const [filter, setFilter] = useState<TariffPlansFilter>({
        currency: defaultCurrency,
        period: "YEARLY",
    });

    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const canManageSubscription = useRoleAccess(CompanyAccess.TariffSubscriptionManage);

    const [isSubscribeModalOpen, setIsSubscribeModalOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<TariffPlanVm | null>(null);
    const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
    const [showCheckboxError, setShowCheckboxError] = useState(false);
    const checkBoxRef = useRef<HTMLDivElement | null>(null);
    const checkBoxInputRef = useRef<HTMLInputElement | null>(null);

    const [generatePaymentUrl] = useGeneratePaymentUrlMutation();

    useOnClickOutside(checkBoxRef, () => setShowCheckboxError(false));

    const [errorModalCode, setErrorModalCode] = useState<number | null>(null);

    const period: PeriodCode = filter.period;

    const { data: plansData, loading: plansLoading } = useGetPlanPriceQuery({
        context: { errorType: "local" },
    });

    const filteredPlanPrices = useMemo(() => {
        const planPrices = plansData?.planPrice ?? [];
        return planPrices.filter((dto) => {
            const prefix = dto.planName.split("_")[0]?.toUpperCase();
            return prefix === "LITE" || prefix === "PREMIUM";
        });
    }, [plansData]);

    const { data: subscriptionsData, loading: subscriptionsLoading } = useGetPaidFeaturesQuery({
        variables: { companyId: +currentCompanyId },
        context: { errorType: "local" },
    });

    const currentSubscription = subscriptionsData?.user?.company?.paidFeatures?.find(
        (subscription) => subscription?.status === "ACTIVE" && subscription?.name
    );

    const currentPlanKey = useMemo<"STARTER" | "LITE" | "PREMIUM" | null>(() => {
        const name: string | undefined = currentSubscription?.name;
        if (!name) return "STARTER";
        const normalized = name.toUpperCase();
        if (normalized.includes("PREMIUM")) return "PREMIUM";
        if (normalized.includes("LITE")) return "LITE";
        return "STARTER";
    }, [currentSubscription]);

    const benefitsRows = useMemo(() => getBenefitsRows(t), [t]);

    const plansVm = useMemo(() => {
        return buildTariffPlansViewModel({
            t,
            planPrices: filteredPlanPrices,
            currencyCode: filter.currency.value,
            period,
            currentPlanKey,
        });
    }, [filteredPlanPrices, t, filter.currency.value, period, currentPlanKey]);

    const handleOpenSubscribeModal = (plan: TariffPlanVm) => {
        if (!canManageSubscription) return;
        if (plan.hideAction) return;
        if (!plan.planId) return;
        if (plan.action?.disabled) return;

        setSelectedPlan(plan);
        setIsCheckboxChecked(false);
        setShowCheckboxError(false);
        setIsSubscribeModalOpen(true);
    };

    const handleCloseSubscribeModal = () => {
        setIsSubscribeModalOpen(false);
        setSelectedPlan(null);
        setIsCheckboxChecked(false);
        setShowCheckboxError(false);
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsCheckboxChecked(event.target.checked);
        if (event.target.checked) setShowCheckboxError(false);
    };

    const handleSubscribe = async () => {
        if (!canManageSubscription) return;
        if (!selectedPlan) return;

        if (!isCheckboxChecked) {
            setShowCheckboxError(true);
            checkBoxInputRef.current?.focus();
            return;
        }

        const input = {
            plan: selectedPlan.planId,
            currency: selectedPlan.currencyCode,
        };

        await generatePaymentUrl({
            variables: {
                companyId: +currentCompanyId,
                input,
                branchId: +branchId,
            },
            context: { errorType: "local" },
            onCompleted: (data) => {
                if (data?.generatePaymentUrl) {
                    window.location.href = data.generatePaymentUrl;
                }
                handleCloseSubscribeModal();
            },
            onError: (error: ApolloError) => {
                setErrorModalCode(ErrorService.errorHandling(error));
            },
        });
    };

    if (subscriptionsLoading || plansLoading || !currentCompanyId) return <Spinner />;

    return (
        <Container fluid className="mx-0 mb-5 px-0">
            <TableHeader
                title={t("Tariff Plans")}
                paths={[
                    {
                        path: `/payment/company/${currentCompanyId}/tariff/${branchId}`,
                        label: t("Management of Tariffs"),
                    },
                    {
                        path: `/payment/company/${currentCompanyId}/tariff/${branchId}`,
                        label: t("Tariff Plans"),
                    },
                ]}
            />

            <TariffPlanFilter onFilterChange={setFilter} hideTitle={false} />

            <div className="mt-7">
                <Row className="d-none d-lg-flex flex-nowrap overflow-auto pb-6">
                    <Col lg={3} className="flex-shrink-0" style={{ minWidth: 300 }}>
                        <TariffPlansBenefitsCard
                            title={t("Plans Benefits")}
                            promoText={
                                period === "YEARLY" ? (
                                    <span className="d-inline-block">
                                        <i className="bi bi-tag-fill text-orange me-1" />
                                        <Trans i18nKey="yearlyPromo" components={{ strong: <strong /> }} />
                                    </span>
                                ) : null
                            }
                            rows={benefitsRows}
                        />
                    </Col>
                    {plansVm.map((plan) => (
                        <Col key={plan.key} lg={3} className="flex-shrink-0" style={{ minWidth: 300 }}>
                            <TariffPlanCard
                                plan={plan}
                                onClickAction={handleOpenSubscribeModal}
                                canManageSubscription={canManageSubscription}
                            />
                        </Col>
                    ))}
                </Row>
                <div className="d-lg-none mt-4">
                    <div className="d-flex flex-nowrap overflow-auto gap-3 pb-6">
                        {plansVm.map((plan) => (
                            <div key={plan.key} className="flex-shrink-0" style={{ width: "min(320px, 90vw)" }}>
                                <TariffPlanCard
                                    plan={plan}
                                    onClickAction={handleOpenSubscribeModal}
                                    canManageSubscription={canManageSubscription}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <InfoFooter />

            <CustomModal
                show={isSubscribeModalOpen}
                handleClose={handleCloseSubscribeModal}
                title={
                    <Container>
                        <Row>
                            <h5 className="m-0 p-0 fw-normal">{t("Registration of Subscription")}</h5>
                        </Row>
                        <Row>
                            <p className="fs-normal fw-normal text-grey p-0 m-0">
                                <span className="me-1">{selectedPlan?.title}</span>
                                <span>
                                    ({period === "YEARLY" ? t("Annual Subscription") : t("Monthly Subscription")})
                                </span>
                            </p>
                        </Row>
                    </Container>
                }
                variantTitle="fs-5"
                body={
                    <Container>
                        <Row className="mb-3">
                            <Col className="d-flex text-start p-0">
                                <span className="fs-6">{t("Payment information")}</span>
                            </Col>
                        </Row>
                        <Row className="fs-7 text-grey py-2">
                            <Col xs={8} className="p-0 text-start">
                                <span className="me-1">{selectedPlan?.title}</span>
                                <span>
                                    ({period === "YEARLY" ? t("Annual Subscription") : t("Monthly Subscription")})
                                </span>
                            </Col>
                            <Col xs={4} className="p-0 text-end">
                                <span className="text-dark">{selectedPlan?.displayPriceText}</span>
                            </Col>
                        </Row>
                        <Row>
                            <hr className="my-2" />
                        </Row>
                        <Row className="fs-7 text-grey py-2 mb-4">
                            <Col xs={8} className="p-0 text-start">
                                <span className="me-1">{t("Total")}</span>
                            </Col>
                            <Col xs={4} className="p-0 text-end">
                                <span className="text-dark fs-6 fw-bold">
                                    {selectedPlan?.paymentAmount?.toFixed(2)}
                                </span>
                                <span className="text-dark fs-6 fw-medium ms-1">{selectedPlan?.currencySymbol}</span>
                            </Col>
                        </Row>
                        <Row ref={checkBoxRef}>
                            <TooltipPopover
                                placement="bottom"
                                text={
                                    <div className="d-flex align-items-center">
                                        <i className="bi bi-exclamation-square-fill text-orange me-2 fs-5"></i>
                                        <span className="fs-7 text-dark">
                                            {t(
                                                "Please check the box to agree to the User Agreement and Privacy Policy"
                                            )}
                                        </span>
                                    </div>
                                }
                                styleBdy="py-1 px-2"
                                show={showCheckboxError}
                                maxWidth="500px"
                                trigger="click"
                            >
                                <Form.Check
                                    id="tariffPlans-agreement-field"
                                    ref={checkBoxInputRef}
                                    className="text-start pe-0"
                                    required
                                    label={
                                        <Form.Check.Label
                                            htmlFor="tariffPlans-agreement-field"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                checkBoxInputRef.current?.focus();
                                            }}
                                        >
                                            <Col className="fs-7">
                                                <span>{t('By clicking "To Payment", you accept')}</span>
                                                &nbsp;
                                                <a
                                                    href={`${basePath}/${language}terms-and-conditions`}
                                                    target="_blank"
                                                    className="text-decoration-none"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <span>{t("User Agreement")}</span>
                                                </a>
                                                &nbsp;
                                                <span>
                                                    {t("and")}
                                                    <br />
                                                </span>
                                                <a
                                                    href={`${basePath}/${language}privacy-policy`}
                                                    className="text-decoration-none"
                                                    target="_blank"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <span>{t("Privacy Policy")}</span>
                                                </a>
                                            </Col>
                                        </Form.Check.Label>
                                    }
                                    onChange={handleCheckboxChange}
                                />
                            </TooltipPopover>
                        </Row>
                    </Container>
                }
                buttons={[
                    {
                        text: t("Cancel"),
                        onClick: handleCloseSubscribeModal,
                        variant: "outline-primary",
                        className: "w-100 fw-normal",
                    },
                    {
                        text: t("To Payment"),
                        onClick: handleSubscribe,
                        variant: "primary",
                        className: "w-100 fw-normal",
                        disabled: !canManageSubscription,
                    },
                ]}
            />

            {errorModalCode ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => setErrorModalCode(null)}
                    isOpen={!!errorModalCode}
                    currentError={errorModalCode}
                />
            ) : null}
        </Container>
    );
};

export default TariffPlans;
